import { useCallback } from 'react';
import { isMobile } from 'react-device-detect';

import { useUser } from 'context/userContext';

import { generateHMAC } from 'utils/generateHMAC';
import useCurrentPage from 'utils/hooks/useCurrentPage';
import { isLocalhost } from 'utils/urlUtils';

export const useIntercom = () => {
  const { userDetails, modules, moduleProjects } = useUser();
  const { isGkp, isSocialMonitoring, isInsights, isInsightsCapture } = useCurrentPage();

  const initiateIntercom = useCallback(async () => {
    let hmac;
    const intercomLauncher = document.getElementsByClassName('intercom-launcher')?.[0];
    const intercomOpened = document.getElementsByClassName('intercom-app')?.[0];
    const { Intercom } = window;
    const debugMode = import.meta.env.VITE_INTERCOM_DEBUG === 'true';
    const shouldInitialize =
      userDetails?.email &&
      (debugMode || !isLocalhost()) &&
      !isMobile &&
      (isGkp || isSocialMonitoring || isInsights || isInsightsCapture);

    if (shouldInitialize) {
      const currentModule = modules?.find(module => module.isCurrent);
      const currentProject = currentModule
        ? moduleProjects(currentModule.key)?.find(
            project => project.projectID === currentModule.currentProject,
          )
        : undefined;
      window.intercomSettings = {
        app_id: 'y588ttna',
        custom_launcher_selector: '#intercom_help_center',
      };
      try {
        hmac = await generateHMAC(userDetails.userId, import.meta.env.VITE_SECRET_KEY);
      } finally {
        Intercom('boot', {
          api_base: 'https://api-iam.intercom.io',
          app_id: 'y588ttna',
          email: userDetails?.email,
          user_id: userDetails?.userId,
          name: userDetails?.name,
          userClientName: userDetails.clientName,
          userClientId: userDetails.clientId,
          created_at: Date.now(),
          user_hash: hmac,
          moduleName: currentModule?.name,
          currentProjectId: currentModule?.currentProject,
          projectClientName: currentProject?.projectClientName,
          projectDisplayName: currentProject?.projectDisplayName,
          company: {
            company_id: userDetails.clientId,
            name: userDetails.clientName,
          },
          hide_default_launcher: false,
        });
      }
    } else if (Intercom) {
      // Hide launcher when conditions aren't met
      Intercom('update', { hide_default_launcher: true });
    }
    // Legacy DOM manipulation for edge cases
    if (intercomLauncher) {
      intercomLauncher.style.visibility = shouldInitialize ? 'visible' : 'hidden';
    }
    if (intercomOpened) {
      intercomOpened.style.visibility = shouldInitialize ? 'visible' : 'hidden';
    }
  }, [
    userDetails,
    isGkp,
    isSocialMonitoring,
    isInsights,
    isInsightsCapture,
    modules,
    moduleProjects,
  ]);

  return { initiateIntercom };
};
